import Link from 'next/link';
import React, { CSSProperties, ReactNode } from 'react';
import { ASSET_PREFIX, colors } from 'utils/constants';

export const ColorStyle = {
	Primary: 'Primary',
	Outline: 'Outline',
	Gray: 'Gray',
  Iceland: 'Iceland',
  OutlineDark: 'OutlineDark',
};

interface RoundedButtonProps {
  href?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
  className?: string;
  id?: string;
  disabled?: boolean;
  backgroundColorOverride?: string;
  colorOverride?: string;
  hoverColor?: string;
  hoverTextColor?: string;
  secondary?: boolean;
  colorStyle?: string;
  linkIsExternal?: boolean;
  loading?: boolean;
  loadingColor?: 'WHITE' | 'BLACK';
  style?: CSSProperties;
  rebrandDm2025?: boolean;
}

const RoundedButton = ({
  href,
  onClick,
  children,
  className,
  id,
  disabled,
  backgroundColorOverride,
  hoverColor,
  hoverTextColor,
  colorOverride,
  secondary = false,
  colorStyle = ColorStyle.Primary,
  linkIsExternal = false,
  loading = false,
  loadingColor = 'WHITE',
  style = {},
  rebrandDm2025,
}: RoundedButtonProps) => {
  let cName = className ? `roundedButton ${className}` : 'roundedButton';
  if (disabled) cName += ' disabled';
  if (loading) cName += ' loading';
  if (rebrandDm2025) cName += ' rebrandDm2025';

  if (secondary) colorStyle = ColorStyle.Outline;
  if (colorStyle == ColorStyle.OutlineDark) {
    colorStyle = ColorStyle.Outline;
    colorOverride = '#4a4a4a';
    hoverColor = "rgba(74, 74, 74, 0.25)";
  }
  let backgroundColor, hoverBackgroundColor;
  switch (colorStyle) {
    case ColorStyle.Primary:
      backgroundColor = backgroundColorOverride || colors.primaryCta;
      hoverBackgroundColor = hoverColor || colors.primaryCtaPressed;
      style.color = colorOverride;
      break;

    case ColorStyle.Outline:
      backgroundColor = backgroundColorOverride || 'clear';
      hoverBackgroundColor = hoverColor || '#FAB8A1';
      style.color = colorOverride || colors.primaryCta;
      style.border = `3px solid ${colorOverride || colors.primaryCta}`;
      if (rebrandDm2025) {
        style.padding = disabled ? '10px 25px' : '17px 22px';
      } else {
        style.padding = disabled ? '10px 25px' : '7px 22px';
      }
      hoverTextColor = 'white';
      break;

    case ColorStyle.Iceland:
      backgroundColor = backgroundColorOverride || colors.icelandRaffle.primary;
      hoverBackgroundColor = hoverColor || colors.icelandRaffle.pressed;
      break;

    case ColorStyle.Gray:
    default:
      backgroundColor = backgroundColorOverride || colors.primaryBrandGray;
      hoverBackgroundColor = hoverColor || colors.primaryBrandGrayPressed;
  }

  return (
    <>
      {href ? (
        linkIsExternal ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            <div
              className={cName}
              style={style}
              id={id}
            >
              {loading ? (
                loadingColor === 'BLACK' ? (
                  <img src={`${ASSET_PREFIX}/images/spinner-black.svg`} />
                ) : (
                  <img src={`${ASSET_PREFIX}/images/spinner-white.svg`} />
                )
              ) : (
                children
              )}
            </div>
          </a>
        ) : (
          <Link href={href}>
            <div
              className={cName}
              style={style}
              id={id}
            >
              {loading ? (
                loadingColor === 'BLACK' ? (
                  <img src={`${ASSET_PREFIX}/images/spinner-black.svg`} />
                ) : (
                  <img src={`${ASSET_PREFIX}/images/spinner-white.svg`} />
                )
              ) : (
                children
              )}
            </div>
          </Link>
        )
      ) : (
        <div
          className={cName}
          onClick={onClick}
          style={style}
          id={id}
        >
          {loading ? (
            loadingColor === 'BLACK' ? (
              <img
                src={`${ASSET_PREFIX}/images/spinner-black.svg`}
                style={{ width: 'auto', height: '1em', alignSelf: 'center' }}
              />
            ) : (
              <img
                src={`${ASSET_PREFIX}/images/spinner-white.svg`}
                style={{ width: 'auto', height: '1em', alignSelf: 'center' }}
              />
            )
          ) : (
            children
          )}
        </div>
      )}

      <style jsx>
        {`
          .roundedButton {
            display: inline-block;
            cursor: pointer;
            padding: 10px 25px;
            text-align: center;
            font-weight: bold;
            white-space: nowrap;
            color: white;
            border-radius: 20px;
            background-color: ${backgroundColor};
            transition: 0.2s;
            text-transform: uppercase;
          }

          .roundedButton:active,
          .roundedButton:hover {
            background-color: ${hoverBackgroundColor};
            color: ${hoverTextColor || 'white'};
          }

          .roundedButton.disabled {
            color: #aaa !important;
            background-color: lightgray;
            pointer-events: none;
            border: none !important;
          }

          .roundedButton.loading {
            pointer-events: none;
          }

          .roundedButton img {
            vertical-align: middle;
          }
          
          .rebrandDm2025 {
            border-radius: 100px;
            line-height: 100%;
            font-size: 16px;
            font-weight: 800;
            padding-top: 16px;
            padding-bottom: 16px;
            /* Min-width of 219px on mobile, but never break out of the container */
            min-width: min(219px, 100%);
          }
          @media (min-width: 768px) {
            .rebrandDm2025 {
              padding-top: 20px;
              padding-bottom: 20px;
              font-size: 20px;
              line-height: 25px;
              /* Min-width of 343px on desktop, but never break out of the container */
              min-width: min(343px, 100%);
            }
          }

        `}
      </style>
    </>
  );
};

export default RoundedButton;
